// Import runtime configuration
import { API_BASE_URL, TRAINING_BASE_URL } from "../config";
import {
  PASSWORD_CHANGE_REQUIRED,
  requirePasswordChange,
  retryAfterUnauthorized,
  storeTokens,
} from "./session";

// A 401 here means bad credentials, not an expired session: don't clear storage or redirect.
const CREDENTIAL_ENDPOINTS = ["/api/auth/login", "/api/auth/cognito"];

// API endpoints
const API_ENDPOINTS = {
  auth: {
    login: "/api/auth/login",
    signup: "/api/auth/signup",
    logout: "/api/auth/logout",
    changePassword: "/api/auth/change-password",
    verifyEmail: "/api/auth/verify-email",
    resendVerification: "/api/auth/resend-verification",
    setPassword: "/api/auth/set-password",
    requestPasswordReset: "/api/auth/request-password-reset",
  },
  projects: "/api/projects",
  conversations: "/api/conversations",
  jira: "/api/jira",
  trace: "/api/trace",
  dashboard: "/api/dashboard",
  applications: "/api/applications",
  reporter: "/api/reporter",
  reports: "/api/reports",
  approvals: "/api/approvals",
  followups: "/api/followups",
  executive: "/api/executive",
  executiveAgent: "/api/executive-agent",
  orgs: "/api/orgs",
  organizations: "/api/organizations",
  providerTypes: "/api/provider-types",
  codifier: "/api/codifier2",
  suppliers: "/api/suppliers",
  builtinTasks: "/api/builtin-tasks",
  domains: "/api/domains",
  workflows: "/api/workflows",
  notifications: "/api/notifications",
  // Base domain-agnostic endpoints
  baseWorkflows: "/api/base/workflows",
  // Automotive domain endpoints
  automotive: {
    classification: "/api/automotive/classification",
    workflows: "/api/base/workflows", // Uses base workflows with domain filter
  },
};

// Helper function to handle API calls
// validation errors: this backend sends {errors: [{field: "body.specs.0.wheels", message}]};
// FastAPI's default is [{loc, msg}]. Either way show "field: message".
const lastSegment = (path) =>
  String(path ?? "")
    .split(".")
    .pop();
const detailText = (detail) => {
  if (Array.isArray(detail?.errors)) {
    return detail.errors.map((e) => `${lastSegment(e.field)}: ${e.message}`).join("; ");
  }
  if (Array.isArray(detail)) {
    return detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join("; ");
  }
  return typeof detail === "string" ? detail : undefined;
};

const fetchApi = async (endpoint, options = {}, _retried = false) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers = {
    ...options.headers,
  };

  // Only set Content-Type to application/json if not explicitly overridden and not FormData
  if (!headers["Content-Type"] && !(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  // Add auth token if available
  const user = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
  if (user.token) {
    headers["Authorization"] = `Bearer ${user.token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("API Error Response:", errorData);
      console.error("Full error details:", JSON.stringify(errorData, null, 2));

      // Handle 401 Unauthorized: refresh once and retry; log out only if the server rejects it
      if (response.status === 401) {
        // Credential endpoints: a 401 just means wrong email/password, not a stale session.
        if (CREDENTIAL_ENDPOINTS.includes(endpoint)) {
          throw new Error(errorData.detail || "Incorrect email or password");
        }
        if (!_retried && !endpoint.startsWith("/api/auth/")) {
          return retryAfterUnauthorized(() => fetchApi(endpoint, options, true));
        }
        console.warn("Authentication failed - token expired or invalid. Logging out...");

        // Clear authentication data
        localStorage.removeItem("user");
        sessionStorage.removeItem("user");

        // Redirect to login page
        window.location.href = "/login";

        throw new Error(errorData.detail || "Session expired. Please login again.");
      }

      if (response.status === 403 && errorData.detail === PASSWORD_CHANGE_REQUIRED) {
        requirePasswordChange();
        throw new Error("You must change your password before continuing");
      }

      throw new Error(detailText(errorData.detail) || `API call failed: ${response.statusText}`);
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

const fetchImage = async (imagePath) => {
  try {
    const response = await fetch(`${API_BASE_URL}${imagePath}`, {
      method: "GET",
      mode: "cors",
      credentials: "same-origin",
    });

    if (!response.ok) {
      throw new Error(`Image fetch failed: ${response.statusText}`);
    }

    return response;
  } catch (error) {
    console.error("Image fetch error:", error);
    throw error;
  }
};

// Generate organization logo URL
const getOrgLogoUrl = (orgId) => {
  if (!orgId) {
    return "";
  }
  return `${API_BASE_URL}${API_ENDPOINTS.orgs}/${encodeURIComponent(orgId)}/logo`;
};

// API methods
// Identical calls made while one is still in flight share it; a later call fetches fresh. Opening
// a CertGPT chat fetched the same conversation's messages 2-3 times in parallel (5-9.5 s each for
// a 3.2 MB payload) and initialized it twice (2026-10-31).
const inFlight = new Map();
const coalesce = (key, run) => {
  if (inFlight.has(key)) {
    return inFlight.get(key);
  }
  const pending = run().finally(() => inFlight.delete(key));
  inFlight.set(key, pending);
  return pending;
};

const api = {
  // Generic HTTP methods
  get: async (endpoint, options = {}) => {
    return fetchApi(endpoint, {
      method: "GET",
      ...options,
    });
  },

  post: async (endpoint, data, options = {}) => {
    return fetchApi(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
      ...options,
    });
  },

  put: async (endpoint, data, options = {}) => {
    return fetchApi(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
      ...options,
    });
  },

  delete: async (endpoint, options = {}) => {
    return fetchApi(endpoint, {
      method: "DELETE",
      ...options,
    });
  },

  // User preferences
  getPreferences: () => fetchApi("/api/auth/me/preferences", { method: "GET" }),
  updatePreferences: (patch) =>
    fetchApi("/api/auth/me/preferences", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  // Auth methods
  login: async (email, password) => {
    try {
      const response = await fetchApi(API_ENDPOINTS.auth.login, {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      return { ...response, tokenIssuedAt: Date.now() };
    } catch (error) {
      console.error("Login error:", error);
      throw new Error(error.message || "Failed to login");
    }
  },

  signup: (data) =>
    fetchApi(API_ENDPOINTS.auth.signup, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  logout: () =>
    fetchApi(API_ENDPOINTS.auth.logout, {
      method: "POST",
    }),

  // the server ends every other session and hands back a fresh one for this browser
  changePassword: (data) =>
    fetchApi(API_ENDPOINTS.auth.changePassword, {
      method: "POST",
      body: JSON.stringify(data),
    }).then((res) => {
      storeTokens(res);
      return res;
    }),

  acceptTerms: (userId, { version, scrolledToEnd, checkbox }) =>
    fetchApi("/api/auth/accept-terms", {
      method: "POST",
      body: JSON.stringify({
        user_id: userId,
        version,
        scrolled_to_end: scrolledToEnd,
        checkbox,
      }),
    }),

  verifyEmail: (token) =>
    fetchApi(`${API_ENDPOINTS.auth.verifyEmail}/${token}`, {
      method: "POST",
    }),

  resendVerification: (email) =>
    fetchApi(API_ENDPOINTS.auth.resendVerification, {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  setPassword: (token, newPassword) =>
    fetchApi(API_ENDPOINTS.auth.setPassword, {
      method: "POST",
      body: JSON.stringify({ token, new_password: newPassword }),
    }),

  requestPasswordReset: (email) =>
    fetchApi(API_ENDPOINTS.auth.requestPasswordReset, {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  // Project methods
  getProjects: () => fetchApi(API_ENDPOINTS.projects),

  getProject: (id) => fetchApi(`${API_ENDPOINTS.projects}/${id}`),

  createProject: (data) =>
    fetchApi(API_ENDPOINTS.projects, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateProject: (id, data) =>
    fetchApi(`${API_ENDPOINTS.projects}/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteProject: (id) =>
    fetchApi(`${API_ENDPOINTS.projects}/${id}`, {
      method: "DELETE",
    }),

  // Conversation methods
  getConversations: (projectId) =>
    fetchApi(`${API_ENDPOINTS.conversations}?project_id=${projectId}`),

  getConversation: (id) => fetchApi(`${API_ENDPOINTS.conversations}/${id}`),

  createConversation: (data) =>
    fetchApi(API_ENDPOINTS.conversations, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateConversation: (id, data) =>
    fetchApi(`${API_ENDPOINTS.conversations}/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteConversation: (id) =>
    fetchApi(`${API_ENDPOINTS.conversations}/${id}`, {
      method: "DELETE",
    }),

  // Organization methods
  getOrgs: () => fetchApi(API_ENDPOINTS.orgs),
  getOrgLogoUrl: (orgId) => getOrgLogoUrl(orgId),

  getOrg: async (orgId) => {
    try {
      const response = await fetchApi(`${API_ENDPOINTS.orgs}/${orgId}`);
      return response;
    } catch (error) {
      console.error("Error fetching organization:", error);
      throw new Error(error.message || "Failed to fetch organization");
    }
  },

  createOrg: (data) =>
    fetchApi(API_ENDPOINTS.orgs, {
      method: "POST",
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),

  updateOrg: (id, data) =>
    fetchApi(`${API_ENDPOINTS.orgs}/${id}`, {
      method: "PUT",
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),

  deleteOrg: (id) =>
    fetchApi(`${API_ENDPOINTS.orgs}/${id}`, {
      method: "DELETE",
    }),

  // Provider config (AI provider / vector DB / file storage per org)
  // Org-scoped routes live under /api/organizations (backend/app/routers/org_provider_config.py),
  // a different base path than /api/orgs used by the org CRUD methods above.
  getProviderTypes: () => fetchApi(API_ENDPOINTS.providerTypes),

  getOrgProviderConfig: (orgId) =>
    fetchApi(`${API_ENDPOINTS.organizations}/${orgId}/provider-config`),

  createOrgProviderConfig: (orgId, data) =>
    fetchApi(`${API_ENDPOINTS.organizations}/${orgId}/provider-config`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateOrgProviderConfig: (orgId, connectionId, data) =>
    fetchApi(`${API_ENDPOINTS.organizations}/${orgId}/provider-config/${connectionId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deleteOrgProviderConfig: (orgId, connectionId) =>
    fetchApi(`${API_ENDPOINTS.organizations}/${orgId}/provider-config/${connectionId}`, {
      method: "DELETE",
    }),

  // Files embedded by an earlier embedding model, and moving them onto the current one
  getReembedStatus: (orgId, { plans = true } = {}) =>
    fetchApi(
      `${API_ENDPOINTS.organizations}/${orgId}/provider-config/embeddings/reembed${plans ? "" : "?plans=false"}`
    ),

  startReembed: (orgId) =>
    fetchApi(`${API_ENDPOINTS.organizations}/${orgId}/provider-config/embeddings/reembed`, {
      method: "POST",
    }),

  submitOrgProviderInput: (orgId, connectionId, secrets) =>
    fetchApi(`${API_ENDPOINTS.organizations}/${orgId}/provider-config/${connectionId}/org-input`, {
      method: "PATCH",
      body: JSON.stringify({ secrets }),
    }),

  // Organization Users
  getOrgUsers: (orgId) => fetchApi(`${API_ENDPOINTS.orgs}/${orgId}/users`),

  addUserToOrg: (orgId, userData) =>
    fetchApi(`${API_ENDPOINTS.orgs}/${orgId}/users`, {
      method: "POST",
      body: JSON.stringify(userData),
    }),

  removeUserFromOrg: (orgId, userId) =>
    fetchApi(`${API_ENDPOINTS.orgs}/${orgId}/users/${userId}`, {
      method: "DELETE",
    }),

  // Trace
  getTraceGraph: (projectId, certNumber, expanded = []) =>
    fetchApi(
      `${API_ENDPOINTS.trace}/project/${projectId}/graph/${certNumber}?${expanded
        .map((id) => `expanded=${id}`)
        .join("&")}`
    ),

  // Codifier Messages
  getConversationsByProject: (projectId) =>
    fetchApi(`${API_ENDPOINTS.conversations}/project/${projectId}`),

  initializeConversation: (projectId, userId) =>
    coalesce(`initialize:${projectId}:${userId}`, () =>
      fetchApi(`${API_ENDPOINTS.codifier}/conversations/project/${projectId}/initialize`, {
        method: "POST",
        // messages ride along: one round trip (and preflight) fewer to open a chat
        body: JSON.stringify({ user_id: userId, include_messages: true }),
      })
    ),

  getConversationTitle: async (projectId) => {
    try {
      // First get the conversation for the project
      const conversations = await fetchApi(`${API_ENDPOINTS.conversations}/project/${projectId}`);
      if (!conversations || conversations.length === 0) {
        return "Untitled Conversation";
      }
      const conversationId = conversations[0].conv_id;

      // Then get the title for that conversation
      const response = await fetchApi(`${API_ENDPOINTS.conversations}/title/${conversationId}`);
      return response.title || "Untitled Conversation";
    } catch (error) {
      console.error("Error fetching conversation title:", error);
      return "Untitled Conversation";
    }
  },

  updateConversationTitle: async (conversationId, newTitle) => {
    return fetchApi(`${API_ENDPOINTS.conversations}/title/${conversationId}`, {
      method: "PATCH",
      body: JSON.stringify({ conversation_name: newTitle }),
    });
  },

  // One answer's graph facts, which the message list leaves out (they made it megabytes)
  getMessageGraphFacts: (convId, msgId) =>
    fetchApi(`${API_ENDPOINTS.codifier}/conversations/${convId}/messages/${msgId}/graph_facts`),

  // One answer's used_hits with their text, which the message list leaves out
  getMessageUsedHits: (convId, msgId) =>
    fetchApi(`${API_ENDPOINTS.codifier}/conversations/${convId}/messages/${msgId}/used_hits`),

  getConversationMessages: async (convId) =>
    coalesce(`messages:${convId}`, () =>
      fetchApi(`${API_ENDPOINTS.codifier}/conversations/${convId}/messages`)
    ),

  // sendChatMessage - only handles regular flow
  sendChatMessage: async (convId, message) => {
    const organization = JSON.parse(localStorage.getItem("organization") || "{}");
    return fetchApi(`/api/codifier2/conversations/${convId}/messages`, {
      method: "POST",
      body: JSON.stringify({
        content: message,
        message_type: "user",
        org_id: organization.org_id,
        domain: organization.type,
        metadata: {
          timestamp: new Date().toISOString(),
        },
      }),
    });
  },

  //  storeMessageWithoutAgent - for confirmation messages and system messages
  storeMessageWithoutAgent: async (
    convId,
    message,
    messageType = "user",
    additionalMetadata = {}
  ) => {
    return fetchApi(`/api/codifier2/conversations/${convId}/messages`, {
      method: "POST",
      body: JSON.stringify({
        content: message,
        message_type: messageType,
        skip_agent: true, // This prevents the agent from being called
        metadata: {
          timestamp: new Date().toISOString(),
          ...additionalMetadata,
        },
      }),
    });
  },

  clearConversationMessages: async (convId) => {
    return fetchApi(`/api/codifier2/conversations/${convId}/messages`, {
      method: "DELETE",
    });
  },

  getDashboardData: () => fetchApi(API_ENDPOINTS.dashboard),

  // Applications
  getApplicationData: async (projectId, type) => {
    return fetchApi(`${API_ENDPOINTS.applications}/project/${projectId}/${type}`);
  },

  getApplicationTypes: async (projectId) => {
    return fetchApi(`${API_ENDPOINTS.applications}/project/${projectId}/types`);
  },

  downloadApplicationTemplate: async (projectId, type) => {
    const response = await fetch(
      `${API_BASE_URL}${API_ENDPOINTS.applications}/project/${projectId}/${type}/download`
    );
    if (!response.ok) {
      throw new Error(`Failed to download template: ${response.statusText}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${type.toLowerCase()}_application_template.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  getBiometricChallenge: async () => {
    const response = await fetchApi("/api/auth/biometric/challenge", {
      method: "POST",
    });
    return response;
  },

  verifyBiometric: async (authResponse) => {
    const response = await fetchApi("/api/auth/biometric/verify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(authResponse),
    });
    return response;
  },

  analyzePrompt: async (prompt, projectId) => {
    try {
      const response = await fetchApi(`${API_ENDPOINTS.reporter}/analyze/${projectId}`, {
        method: "POST",
        body: JSON.stringify({ prompt }),
      });
      return response;
    } catch (error) {
      console.error("Error analyzing prompt:", error);
      throw error;
    }
  },

  getRisksAndErrors: () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const orgId = user?.org_id;

    if (!orgId) {
      console.error("No org_id found in user context");
      return Promise.reject(new Error("Organization ID is required"));
    }

    return fetchApi(`/api/risk-error/analyses?org_id=${orgId}`, {
      method: "GET",
    });
  },

  analyzeAllRisksAndErrors: (projectId = null) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const orgId = user?.org_id;

    if (!orgId) {
      console.error("No org_id found in user context");
      return Promise.reject(new Error("Organization ID is required"));
    }

    const params = new URLSearchParams();
    params.append("org_id", orgId);
    if (projectId) {
      params.append("project_id", projectId);
    }
    params.append("domain", "general");

    return fetchApi(`/api/risk-error/analyze-organization?${params.toString()}`, {
      method: "POST",
    });
  },

  analyzeJiraTasks: (projectId = null) => {
    // Get org_id from localStorage
    const user = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
    const orgId = user?.org_id;

    if (!orgId) {
      console.error("No org_id found in user context");
      return Promise.reject(new Error("Organization ID is required"));
    }

    const params = new URLSearchParams();
    params.append("org_id", orgId);
    if (projectId) {
      params.append("project_id", projectId);
    }
    params.append("domain", "general");

    return fetchApi(`/api/risk-error/analyze-organization?${params.toString()}`, {
      method: "POST",
    });
  },

  getRecentCompletedTasks: (projectId = null, limit = 5) => {
    // Get org_id from localStorage
    const user = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
    const orgId = user?.org_id;

    if (!orgId) {
      console.error("No org_id found in user context");
      return Promise.reject(new Error("Organization ID is required"));
    }

    const params = new URLSearchParams();
    params.append("org_id", orgId);
    if (projectId) {
      params.append("project_id", projectId);
    }
    params.append("limit", limit.toString());

    return fetchApi(`/api/risk-error/recent-completed-tasks?${params.toString()}`);
  },

  // Reports
  getReports: async (projectId) => {
    return fetchApi(`${API_ENDPOINTS.reports}/project/${projectId}`);
  },

  // Database-based Reports
  createReport: async (reportData) => {
    return fetchApi(`${API_ENDPOINTS.reports}/`, {
      method: "POST",
      body: JSON.stringify(reportData),
    });
  },

  autoAssembleReports: async (projectId) => {
    return fetchApi(`${API_ENDPOINTS.reports}/db/project/${projectId}/auto-assemble`, {
      method: "POST",
    });
  },

  autoAssembleReportsPreview: async (projectId) => {
    return fetchApi(`${API_ENDPOINTS.reports}/db/project/${projectId}/auto-assemble/preview`, {
      method: "POST",
    });
  },

  autoAssembleReportsConfirm: async (projectId, selectedReports) => {
    return fetchApi(`${API_ENDPOINTS.reports}/db/project/${projectId}/auto-assemble/confirm`, {
      method: "POST",
      body: JSON.stringify({ reports: selectedReports }),
    });
  },

  getReport: async (reportId) => {
    return fetchApi(`${API_ENDPOINTS.reports}/db/${reportId}`);
  },

  getProjectReports: async (projectId) => {
    return fetchApi(`${API_ENDPOINTS.reports}/db/project/${projectId}`);
  },

  getOrgReports: async (orgId) => {
    return fetchApi(`${API_ENDPOINTS.reports}/db/org/${orgId}`);
  },

  updateReport: async (reportId, updateData) => {
    return fetchApi(`${API_ENDPOINTS.reports}/db/${reportId}`, {
      method: "PUT",
      body: JSON.stringify(updateData),
    });
  },

  signReport: async (reportId, signatureData, signerName, signerRole = null) => {
    return fetchApi(`${API_ENDPOINTS.reports}/db/${reportId}/sign`, {
      method: "POST",
      body: JSON.stringify({
        signature_data: signatureData,
        signer_name: signerName,
        signer_role: signerRole,
      }),
    });
  },

  deleteReport: async (reportId) => {
    return fetchApi(`${API_ENDPOINTS.reports}/db/${reportId}`, {
      method: "DELETE",
    });
  },

  cleanupDuplicateReports: async (projectId = null) => {
    const params = projectId ? `?project_id=${projectId}` : "";
    return fetchApi(`${API_ENDPOINTS.reports}/db/cleanup-duplicates${params}`, {
      method: "POST",
    });
  },

  getSignedReports: async (orgId) => {
    return fetchApi(`${API_ENDPOINTS.reports}/db/signed/${orgId}`);
  },

  // Approvals
  getApprovals: async (projectId) => {
    return fetchApi(`${API_ENDPOINTS.approvals}/project/${projectId}`);
  },

  // Followups
  getFollowups: async (projectId) => {
    return fetchApi(`${API_ENDPOINTS.followups}/project/${projectId}`);
  },

  // Notifications
  getNotifications: (limit = 50, unreadOnly = false) => {
    return fetchApi(`${API_ENDPOINTS.notifications}/?limit=${limit}&unread_only=${unreadOnly}`);
  },

  markNotificationRead: (id) => {
    return fetchApi(`${API_ENDPOINTS.notifications}/${id}/read`, { method: "PUT" });
  },

  markAllNotificationsRead: () => {
    return fetchApi(`${API_ENDPOINTS.notifications}/mark-all-read`, { method: "POST" });
  },

  // Executive Report
  getExecutiveReport: (projectId) => fetchApi(`${API_ENDPOINTS.executive}/report/${projectId}`),

  queryExecutiveAgent: async (projectId, query) => {
    return fetchApi(`${API_ENDPOINTS.executiveAgent}/query`, {
      method: "POST",
      body: JSON.stringify({ project_id: projectId, query }),
    });
  },

  generateTaskDescription: async (prompt, taskData) => {
    const response = await fetchApi(`${API_ENDPOINTS.jira}/tasks/generate-description`, {
      method: "POST",
      body: JSON.stringify({
        prompt,
        taskId: taskData.task_id,
        title: taskData.title,
        level: taskData.level,
        role: taskData.role,
      }),
    });
    return {
      description: response.description,
      title: response.title || taskData.title, // Fallback to existing title if none provided
    };
  },

  // Document Store methods
  getCorpusStats: async (orgId = null, domain = null) => {
    try {
      const params = new URLSearchParams();
      if (orgId) {
        params.append("org_id", orgId);
      }
      if (domain) {
        params.append("domain", domain);
      }

      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/train/stats?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        console.error("Stats API error response:", error);
        throw new Error(error.detail || "Failed to fetch corpus statistics");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching corpus stats:", error);
      throw error;
    }
  },

  // Get S3 files for an organization
  getS3Files: async (orgId = null, domain = null) => {
    try {
      const params = new URLSearchParams();
      if (orgId) {
        params.append("org_id", orgId);
      }
      if (domain) {
        params.append("domain", domain);
      }

      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/train/s3-files/?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        console.error("S3 Files API error response:", error);
        throw new Error(error.detail || "Failed to fetch S3 files");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching S3 files:", error);
      throw error;
    }
  },

  // Delete a file from S3 and index
  deleteFile: async (fileName, orgId = null, domain = null) => {
    try {
      const params = new URLSearchParams();
      params.append("files_name", fileName);
      if (orgId) {
        params.append("org_id", orgId);
      }
      if (domain) {
        params.append("domain", domain);
      }

      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/train/delete_file?${params.toString()}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        console.error("Delete file API error response:", error);
        throw new Error(error.detail || "Failed to delete file");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error deleting file:", error);
      throw error;
    }
  },

  //upload document api

  uploadDocument: async (formData) => {
    try {
      const endpoint = "/api/train/from-pdf";
      // Always use training service URL for training endpoints
      // Since backend and training are on separate servers
      const url = `${TRAINING_BASE_URL}${endpoint}`;

      // Get auth token
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: user?.token ? `Bearer ${user.token}` : "",
          Accept: "application/json",
        },
        body: formData,
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {
        console.error("Error parsing JSON response:", e);
        const text = await response.text();
        console.error("Raw response:", text);
        throw new Error(`Failed to parse response: ${text}`);
      }

      if (!response.ok) {
        const errorMessage =
          responseData?.detail ||
          responseData?.message ||
          `Upload failed with status ${response.status}`;
        console.error("Upload failed:", errorMessage);
        const error = new Error(errorMessage);
        error.response = { data: responseData, status: response.status };
        throw error;
      }

      return responseData;
    } catch (error) {
      console.error("Upload error:", error);
      if (!error.response) {
        error.response = { data: { detail: error.message }, status: 0 };
      }
      throw error;
    }
  },

  discoverHtmlLeafIds: async (params) => {
    try {
      const url = `${TRAINING_BASE_URL}/api/train/from-html/discover`;
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );
      const fd = new FormData();
      fd.append("title", params.title);
      fd.append("leaf_level", params.leafLevel);
      ["subtitle", "chapter", "subchapter", "part", "subpart"].forEach((k) => {
        if (params[k]) {
          fd.append(k, params[k]);
        }
      });
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
        body: fd,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Discover failed: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error discovering HTML leaf IDs:", error);
      throw error;
    }
  },

  uploadHtmlDocuments: async (formData) => {
    try {
      const url = `${TRAINING_BASE_URL}/api/train/from-html/bulk`;
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
        body: formData,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Upload failed: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error submitting HTML ingestion:", error);
      throw error;
    }
  },

  // Ingest web links (eCFR, EUR-Lex, or any page) into an org or a domain corpus
  ingestLinks: async function ({ urls, orgId, domain, extractGraph = true, extraMeta = {} }) {
    const fd = new FormData();
    urls.forEach((u) => fd.append("urls", u));
    fd.append(orgId ? "org_id" : "domain", orgId || domain);
    fd.append("extract_graph", extractGraph ? "true" : "false");
    Object.entries(extraMeta).forEach(
      ([k, v]) => v !== undefined && v !== null && v !== "" && fd.append(k, v)
    );
    const token = this.getToken();
    const response = await fetch(`${TRAINING_BASE_URL}/api/train/from-html`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // FastAPI validation errors carry detail as a list of {msg, loc}
      const detail = Array.isArray(errorData.detail)
        ? errorData.detail.map((d) => d.msg).join("; ")
        : errorData.detail;
      throw new Error(detail || `Link ingestion failed: ${response.statusText}`);
    }
    return await response.json();
  },

  // Helper function to get auth token
  getToken: () => {
    const user = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
    return user?.token || "";
  },

  // Re-ingest stored files; old chunks stay searchable until the new ones are indexed
  reprocessOrgFiles: async function (fileIds) {
    const response = await fetch(`${TRAINING_BASE_URL}/api/train/reprocess-files`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ file_ids: fileIds }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Reprocess failed: ${response.statusText}`);
    }
    return await response.json();
  },

  // Latest worker stage per file: {file_id: {status, progress, message, new_chunks, ...}}
  getFileProgress: async function (fileIds) {
    const params = new URLSearchParams();
    fileIds.forEach((id) => params.append("file_ids", id));
    const response = await fetch(`${TRAINING_BASE_URL}/api/train/file-progress?${params}`, {
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });
    if (!response.ok) {
      throw new Error(`File progress failed: ${response.statusText}`);
    }
    return await response.json();
  },

  // Training job status check
  checkTrainingJobStatus: async function (jobId) {
    const url = `${TRAINING_BASE_URL}/api/train/jobs/${jobId}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.getToken()}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to check job status: ${response.statusText}`);
    }

    return await response.json();
  },

  retryJob: async function (jobId) {
    const url = `${TRAINING_BASE_URL}/api/train/jobs/${jobId}/retry`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.getToken()}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to retry job: ${response.statusText}`);
    }

    return await response.json();
  },

  // Ingest DRS document for training
  ingestDrsDocument: async function (drsDocData) {
    try {
      const url = `${TRAINING_BASE_URL}/api/train/ingest-drs`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          url: drsDocData.documentUrl || drsDocData.url, // Fix: support both field names
          file_id: drsDocData.fileId, // Add file_id for backend DRS download
          title: drsDocData.title,
          document_number: drsDocData.documentNumber,
          doc_type: drsDocData.docType,
          org_id: drsDocData.orgId,
          user_access: drsDocData.userAccess || "all",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.detail || `Failed to ingest DRS document with status ${response.status}`;
        console.error("DRS ingestion failed:", errorMessage);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("DRS ingestion error:", error);
      throw error;
    }
  },

  checkDrsDocumentStatus: async function (documents, orgId) {
    try {
      const url = `${TRAINING_BASE_URL}/api/train/check-drs-status`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          documents: documents,
          org_id: orgId,
        }),
      });

      if (!response.ok) {
        console.error("Failed to check DRS document status");
        return { documents: [] };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error checking DRS document status:", error);
      return { documents: [] };
    }
  },

  //jira configuration call

  getJiraConfig: () => fetchApi(`${API_ENDPOINTS.jira}/config`),

  deleteJiraConfig: () =>
    fetchApi(`${API_ENDPOINTS.jira}/config`, {
      method: "DELETE",
    }),

  updateJiraConfig: (config) =>
    fetchApi(`${API_ENDPOINTS.jira}/config`, {
      method: "POST",
      body: JSON.stringify(config),
    }),

  testJiraConnection: () =>
    fetchApi(`${API_ENDPOINTS.jira}/test-connection`, {
      method: "POST",
    }),

  // JIRA Tasks
  getJiraTasks: () => fetchApi(`${API_ENDPOINTS.jira}/tasks`),

  getProjectTasks: (projectId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status && params.status !== "all") {
      queryParams.append("status", params.status);
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }

    const queryString = queryParams.toString();
    const url = `${API_ENDPOINTS.jira}/tasks/project/${projectId}${
      queryString ? `?${queryString}` : ""
    }`;
    return fetchApi(url);
  },

  getJiraTask: (taskId) => fetchApi(`${API_ENDPOINTS.jira}/tasks/${taskId}`),

  // Create single JIRA task through backend
  createJiraTask: (taskData) =>
    fetchApi(`${API_ENDPOINTS.jira}/tasks`, {
      method: "POST",
      body: JSON.stringify(taskData),
    }),

  // Create multiple JIRA tasks from prompt through backend
  createJiraTasks: (projectId, prompt) =>
    fetchApi(`${API_ENDPOINTS.jira}/tasks/project/${projectId}`, {
      method: "POST",
      body: JSON.stringify({ prompt }),
    }),

  // Update JIRA task through backend
  updateJiraTask: (taskId, updates) =>
    fetchApi(`${API_ENDPOINTS.jira}/tasks/${taskId}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    }),

  // Delete JIRA task through backend
  deleteJiraTask: (taskId) =>
    fetchApi(`${API_ENDPOINTS.jira}/tasks/${taskId}`, {
      method: "DELETE",
    }),

  // Delete supplier task
  deleteSupplierTask: (taskId) =>
    fetchApi(`${API_ENDPOINTS.suppliers}/tasks/${taskId}`, {
      method: "DELETE",
    }),

  // JIRA Sync - Refresh from JIRA through backend
  syncProjectFromJira: (projectId) =>
    fetchApi(`${API_ENDPOINTS.jira}/sync/project/${projectId}`, {
      method: "POST",
    }),

  syncAllFromJira: () =>
    fetchApi(`${API_ENDPOINTS.jira}/sync/all`, {
      method: "POST",
    }),

  getJiraSyncStatus: (projectId = null) => {
    const url = projectId
      ? `${API_ENDPOINTS.jira}/sync/status?project_id=${projectId}`
      : `${API_ENDPOINTS.jira}/sync/status`;
    return fetchApi(url);
  },

  // Get regulatory news for organization type
  getRegulatoryNews: (orgType) => fetchApi(`${API_ENDPOINTS.dashboard}/regulatory-news/${orgType}`),

  // Get assignable users for a JIRA issue
  getJiraAssignableUsers: (issueId) =>
    fetchApi(`${API_ENDPOINTS.jira}/tasks/${issueId}/assignable-users`),

  // Get comments for a JIRA task
  getJiraTaskComments: (taskId) => fetchApi(`${API_ENDPOINTS.jira}/tasks/${taskId}/comments`),

  // Create comment for a JIRA task
  createJiraTaskComment: (taskId, commentData) =>
    fetchApi(`${API_ENDPOINTS.jira}/tasks/${taskId}/comments`, {
      method: "POST",
      body: JSON.stringify(commentData),
    }),

  // Get attachments for a JIRA task
  getJiraTaskAttachments: (taskId) => fetchApi(`${API_ENDPOINTS.jira}/tasks/${taskId}/attachments`),

  // Create attachment for a JIRA task
  createJiraTaskAttachment: (taskId, attachmentData) => {
    const formData = new FormData();
    formData.append("file", attachmentData.file);
    if (attachmentData.comment) {
      formData.append("comment", attachmentData.comment);
    }

    return fetch(`${API_BASE_URL}${API_ENDPOINTS.jira}/tasks/${taskId}/attachments`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}").token || ""}`,
      },
      body: formData,
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    });
  },

  // Get project task statistics through backend
  getProjectTaskStats: (projectId) => fetchApi(`${API_ENDPOINTS.jira}/projects/${projectId}/stats`),

  // Update the backward compatibility method
  updateTask: (taskId, updates) => {
    console.warn("api.updateTask is deprecated, use api.updateJiraTask instead");
    return fetchApi(`${API_ENDPOINTS.jira}/tasks/${taskId}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  },
  // Share message via email
  shareMessage: (shareData) => {
    return fetchApi("/api/send/email", {
      method: "POST",
      body: JSON.stringify({
        query: shareData.message,
        response: shareData.additionalMessage || "",
        recipient_email: shareData.email,
      }),
    });
  },

  updateOrganization: (orgId, type) =>
    fetchApi(`${API_ENDPOINTS.orgs}/${orgId}/type`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ type }),
    }),

  getJiraAttachmentContent: async (attachmentId) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.jira}/attachment/${attachmentId}/content`,
        {
          method: "GET",
          headers: {
            Authorization: user?.token ? `Bearer ${user.token}` : "",
            Accept: "application/pdf, */*",
          },
          mode: "cors",
          credentials: "include",
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch attachment: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      return blobUrl;
    } catch (error) {
      console.error("Error fetching Jira attachment:", error);
      throw error;
    }
  },

  // Message feedback method
  addMessageFeedback: async (convId, msgId, reaction, comment = null) => {
    try {
      const payload = { reaction };
      if (comment) {
        payload.comment = comment;
      }

      const response = await fetchApi(
        `${API_ENDPOINTS.codifier}/conversations/${convId}/messages/${msgId}/feedback`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
        }
      );

      return response;
    } catch (error) {
      console.error("Error adding message feedback:", error);
      throw error;
    }
  },

  // Supplier APIs
  getSuppliers: (status, search) => {
    const params = new URLSearchParams();
    if (status && status !== "all") {
      params.append("status", status);
    }
    if (search) {
      params.append("search", search);
    }
    const queryString = params.toString();
    return fetchApi(`/api/suppliers${queryString ? `?${queryString}` : ""}`);
  },

  createSupplier: (supplierData) =>
    fetchApi("/api/suppliers", {
      method: "POST",
      body: JSON.stringify(supplierData),
    }),

  getSupplier: (supplierId) => fetchApi(`/api/suppliers/${supplierId}`),

  updateSupplier: (supplierId, supplierData) =>
    fetchApi(`/api/suppliers/${supplierId}`, {
      method: "PUT",
      body: JSON.stringify(supplierData),
    }),

  deleteSupplier: (supplierId) =>
    fetchApi(`/api/suppliers/${supplierId}`, {
      method: "DELETE",
    }),

  onboardSupplier: (supplierId, data) =>
    fetchApi(`/api/suppliers/${supplierId}/onboard`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // Complete onboarding (for supplier users)
  completeOnboarding: (onboardingData) =>
    fetchApi("/api/suppliers/complete-onboarding", {
      method: "POST",
      body: JSON.stringify(onboardingData),
    }),

  // Approve/Reject onboarding (for parent org)
  approveOnboarding: (supplierId) =>
    fetchApi(`/api/suppliers/${supplierId}/approve-onboarding`, {
      method: "POST",
    }),

  rejectOnboarding: (supplierId, reason) =>
    fetchApi(`/api/suppliers/${supplierId}/reject-onboarding`, {
      method: "POST",
      body: JSON.stringify(reason),
    }),

  // Get supplier task details (for parent org)
  getSupplierTaskDetails: (supplierId, taskId) =>
    fetchApi(`/api/suppliers/${supplierId}/task-details/${taskId}`),

  // Get all supplier documents (for parent org)
  getSupplierAllDocuments: (supplierId) => fetchApi(`/api/suppliers/${supplierId}/all-documents`),

  // Supplier Tasks (for parent org)
  getSupplierTasks: (supplierId, status) => {
    const params = new URLSearchParams();
    if (status && status !== "all") {
      params.append("status", status);
    }
    const queryString = params.toString();
    return fetchApi(`/api/suppliers/${supplierId}/tasks${queryString ? `?${queryString}` : ""}`);
  },

  createSupplierTask: (supplierId, taskData) =>
    fetchApi(`/api/suppliers/${supplierId}/tasks`, {
      method: "POST",
      body: JSON.stringify(taskData),
    }),

  updateSupplierTask: (taskId, taskData) =>
    fetchApi(`/api/suppliers/tasks/${taskId}`, {
      method: "PUT",
      body: JSON.stringify(taskData),
    }),

  // My Tasks (for supplier users)
  getMyTasks: () => fetchApi("/api/suppliers/tasks"),

  // Task Dependencies
  getTaskDependencies: (taskId, taskType) =>
    fetchApi(`/api/task-dependencies/tasks/${taskId}/dependencies?task_type=${taskType}`),

  getTaskDependents: (taskId, taskType) =>
    fetchApi(`/api/task-dependencies/tasks/${taskId}/dependents?task_type=${taskType}`),

  getJiraTaskLinks: (taskId, taskType) =>
    fetchApi(`/api/task-dependencies/tasks/${taskId}/jira-links?task_type=${taskType}`),

  getMyTask: (taskId) => fetchApi(`/api/suppliers/tasks/${taskId}`),

  // Get any task (for parent/OEM users)
  getTask: (taskId) => fetchApi(`/api/suppliers/tasks/${taskId}/general`),

  updateMyTask: (taskId, taskData) =>
    fetchApi(`/api/suppliers/tasks/${taskId}`, {
      method: "PUT",
      body: JSON.stringify(taskData),
    }),

  // My Profile (for supplier users)
  getMyProfile: () => fetchApi("/api/suppliers/profile"),

  // Task Documents (for supplier users)
  getTaskDocuments: (taskId) => fetchApi(`/api/suppliers/tasks/${taskId}/documents`),

  removeDocumentFromTask: (taskId, documentId) =>
    fetchApi(`/api/suppliers/tasks/${taskId}/documents/${documentId}`, {
      method: "DELETE",
    }),

  attachDocumentToTask: (taskId, documentId) =>
    fetchApi(`/api/suppliers/tasks/${taskId}/documents/${documentId}/attach`, {
      method: "PUT",
    }),

  // Supplier Documents (for parent org)
  getSupplierDocuments: (supplierId) => fetchApi(`/api/suppliers/${supplierId}/documents`),

  uploadSupplierDocument: (supplierId, formData) =>
    fetchApi(`/api/suppliers/${supplierId}/documents`, {
      method: "POST",
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    }),

  // My Documents (for supplier users)
  getMyDocuments: () => fetchApi("/api/suppliers/documents"),

  uploadMyDocument: (formData) =>
    fetchApi("/api/suppliers/documents", {
      method: "POST",
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    }),

  downloadMyDocument: async (documentId) => {
    const response = await fetch(`${API_BASE_URL}/api/suppliers/documents/${documentId}/download`, {
      headers: {
        Authorization: `Bearer ${JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}").token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }

    return response;
  },

  deleteMyDocument: (documentId) =>
    fetchApi(`/api/suppliers/documents/${documentId}`, {
      method: "DELETE",
    }),

  downloadSupplierDocument: async (supplierId, documentId) => {
    const response = await fetch(
      `${API_BASE_URL}/api/suppliers/${supplierId}/documents/${documentId}/download`,
      {
        headers: {
          Authorization: `Bearer ${JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}").token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }

    return response;
  },

  deleteSupplierDocument: (supplierId, documentId) =>
    fetchApi(`/api/suppliers/${supplierId}/documents/${documentId}`, {
      method: "DELETE",
    }),

  // ============== SUPPLIER ANALYSIS API METHODS ==============

  // Trigger AI analysis for supplier task
  analyzeSupplierTask: (taskId) =>
    fetchApi(`/api/supplier-analysis/${taskId}/analyze`, {
      method: "POST",
    }),

  // Get latest analysis results
  getSupplierTaskAnalysis: (taskId) => fetchApi(`/api/supplier-analysis/${taskId}/analysis`),

  // Get analysis history
  getSupplierTaskAnalysisHistory: (taskId) =>
    fetchApi(`/api/supplier-analysis/${taskId}/analysis/history`),

  // ============== TRACEABILITY API METHODS ==============

  // Traceability Tasks
  getTraceabilityTasks: (status, supplierId) => {
    const params = new URLSearchParams();
    if (status && status !== "all") {
      params.append("status", status);
    }
    if (supplierId) {
      params.append("supplier_id", supplierId);
    }
    const queryString = params.toString();
    return fetchApi(`/api/traceability/tasks${queryString ? `?${queryString}` : ""}`);
  },

  createTraceabilityTask: (taskData) =>
    fetchApi("/api/traceability/tasks", {
      method: "POST",
      body: JSON.stringify(taskData),
    }),

  getTraceabilityTask: (taskId) => fetchApi(`/api/traceability/tasks/${taskId}`),

  updateTraceabilityTask: (taskId, taskData) =>
    fetchApi(`/api/traceability/tasks/${taskId}`, {
      method: "PUT",
      body: JSON.stringify(taskData),
    }),

  // Removed traceability response, evidence, and validation endpoints as these tables are unused

  // Approvals
  submitApproval: (taskId, approvalData) =>
    fetchApi(`/api/traceability/tasks/${taskId}/approve`, {
      method: "POST",
      body: JSON.stringify(approvalData),
    }),

  // Traceability Chain and Graph
  getTraceabilityChain: (taskId) => fetchApi(`/api/traceability/tasks/${taskId}/chain`),

  getTraceabilityGraph: (taskId) => fetchApi(`/api/traceability/tasks/${taskId}/graph`),

  // Statistics
  getTraceabilityStats: () => fetchApi("/api/traceability/stats"),

  // Get approvals for a task
  getTraceabilityApprovals: (taskId) => fetchApi(`/api/traceability/tasks/${taskId}/approvals`),

  // Clear approvals
  clearTaskApprovals: (taskId) =>
    fetchApi(`/api/traceability/tasks/${taskId}/approvals`, {
      method: "DELETE",
    }),

  // Org Files APIs - New database-driven file management
  getOrgFiles: async (
    orgId = null,
    domain = null,
    status = null,
    indexed = null,
    limit = 100,
    offset = 0
  ) => {
    try {
      const params = new URLSearchParams();

      // Require either orgId or domain, but not both
      if (!orgId && !domain) {
        throw new Error("Either orgId or domain parameter must be provided");
      }
      if (orgId && domain) {
        throw new Error("Cannot specify both orgId and domain parameters");
      }

      if (orgId) {
        params.append("org_id", orgId);
      } else {
        params.append("domain", domain);
      }

      if (status) {
        params.append("status", status);
      }
      if (indexed !== null) {
        params.append("indexed", indexed.toString());
      }
      params.append("limit", limit.toString());
      params.append("offset", offset.toString());

      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/org-files?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to fetch org files");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching org files:", error);
      throw error;
    }
  },

  getOrgFilesStats: async (orgId = null, domain = null) => {
    try {
      const params = new URLSearchParams();

      // Require either orgId or domain, but not both
      if (!orgId && !domain) {
        throw new Error("Either orgId or domain parameter must be provided");
      }
      if (orgId && domain) {
        throw new Error("Cannot specify both orgId and domain parameters");
      }

      if (orgId) {
        params.append("org_id", orgId);
      } else {
        params.append("domain", domain);
      }

      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/org-files/stats?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to fetch org files stats");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching org files stats:", error);
      throw error;
    }
  },

  downloadOrgFile: async (fileId) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/org-files/${fileId}/download`, {
        method: "GET",
        headers: {
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to get download URL");
      }

      const data = await response.json();
      return data; // Return the full JSON object
    } catch (error) {
      console.error("Error getting download URL:", error);
      throw error;
    }
  },

  previewOrgFile: async (fileId) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/org-files/${fileId}/preview`, {
        method: "GET",
        headers: {
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to get preview URL");
      }

      const data = await response.json();
      return data; // Return the full JSON object
    } catch (error) {
      console.error("Error getting preview URL:", error);
      throw error;
    }
  },

  deleteOrgFile: async (fileId) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/org-files/${fileId}`, {
        method: "DELETE",
        headers: {
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to delete file");
      }

      return await response.json();
    } catch (error) {
      console.error("Error deleting org file:", error);
      throw error;
    }
  },

  // Same-origin PDF bytes for a file (bypasses S3 CORS). Returns an object URL.
  getOrgFilePdfObjectUrl: async (fileId) => {
    const user = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
    const response = await fetch(`${API_BASE_URL}/api/org-files/${fileId}/pdf`, {
      method: "GET",
      headers: { Authorization: user?.token ? `Bearer ${user.token}` : "" },
    });
    if (!response.ok) {
      throw new Error(`Failed to load PDF (${response.status})`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  // List a file's chunks with summary + auto/user keywords/questions (chunk viewer)
  getFileChunks: async (fileId) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );
      const response = await fetch(`${API_BASE_URL}/api/org-files/${fileId}/chunks`, {
        method: "GET",
        headers: { Authorization: user?.token ? `Bearer ${user.token}` : "" },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to load chunks");
      }
      return await response.json();
    } catch (error) {
      console.error("Error loading file chunks:", error);
      throw error;
    }
  },

  // Load the extracted graph (nodes + edges) for a document.
  // Defaults to the legible view (concept layer + cross-doc nodes hidden). Pass
  // { includeConcepts, includeCrossDoc } to restore the full graph.
  getFileGraph: async (fileId, { includeConcepts = false, includeCrossDoc = false } = {}) => {
    const user = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
    const p = new URLSearchParams();
    if (includeConcepts) {
      p.set("include_concepts", "true");
    }
    if (includeCrossDoc) {
      p.set("include_cross_doc", "true");
    }
    const qs = p.toString() ? `?${p}` : "";
    const response = await fetch(`${API_BASE_URL}/api/org-files/${fileId}/graph${qs}`, {
      method: "GET",
      headers: { Authorization: user?.token ? `Bearer ${user.token}` : "" },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to load graph");
    }
    return await response.json();
  },

  // Load the chunks a graph node maps to (via its source_ref filename+section).
  getGraphNodeChunks: async (fileId, nodeId) => {
    const user = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
    const response = await fetch(
      `${API_BASE_URL}/api/org-files/${fileId}/graph/nodes/${nodeId}/chunks`,
      { method: "GET", headers: { Authorization: user?.token ? `Bearer ${user.token}` : "" } }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to load node chunks");
    }
    return await response.json();
  },

  // ---- Knowledge graph admin (view/edit/add/remove + induced extract-review-merge) ----
  graphAdmin: {
    _hdr: () => {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );
      return { Authorization: user?.token ? `Bearer ${user.token}` : "" };
    },
    async _json(path, opts = {}) {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );
      const resp = await fetch(`${API_BASE_URL}/api/graph-admin${path}`, {
        ...opts,
        headers: {
          ...(opts.body ? { "Content-Type": "application/json" } : {}),
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });
      if (!resp.ok) {
        let detail = `HTTP ${resp.status}`;
        try {
          detail = (await resp.json()).detail || detail;
        } catch (_e) {
          /* ignore */
        }
        throw new Error(detail);
      }
      return resp.json();
    },
    getDomainGraph({ target_domain = null, target_org_id = null, source_document = null } = {}) {
      const p = new URLSearchParams();
      if (target_domain) {
        p.set("target_domain", target_domain);
      }
      if (target_org_id) {
        p.set("target_org_id", target_org_id);
      }
      if (source_document) {
        p.set("source_document", source_document);
      }
      return this._json(`/domain-graph${p.toString() ? `?${p}` : ""}`);
    },
    getTemplate({ target_domain = null, target_org_id = null } = {}) {
      const p = new URLSearchParams();
      if (target_domain) {
        p.set("target_domain", target_domain);
      }
      if (target_org_id) {
        p.set("target_org_id", target_org_id);
      }
      return this._json(`/template${p.toString() ? `?${p}` : ""}`);
    },
    createNode(node) {
      return this._json("/nodes", { method: "POST", body: JSON.stringify(node) });
    },
    updateNode(nodeId, patch) {
      return this._json(`/nodes/${nodeId}`, { method: "PUT", body: JSON.stringify(patch) });
    },
    deleteNode(nodeId) {
      return this._json(`/nodes/${nodeId}`, { method: "DELETE" });
    },
    createEdge(edge) {
      return this._json("/edges", { method: "POST", body: JSON.stringify(edge) });
    },
    deleteEdge(edgeId) {
      return this._json(`/edges/${edgeId}`, { method: "DELETE" });
    },
    extractPreview({ file_id, target_domain = null, target_org_id = null }) {
      return this._json("/extract-preview", {
        method: "POST",
        body: JSON.stringify({ file_id, target_domain, target_org_id }),
      });
    },
    async extractUpload(file, { target_domain = null, target_org_id = null } = {}) {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );
      const fd = new FormData();
      fd.append("file", file);
      if (target_domain) {
        fd.append("target_domain", target_domain);
      }
      if (target_org_id) {
        fd.append("target_org_id", target_org_id);
      }
      const resp = await fetch(`${API_BASE_URL}/api/graph-admin/extract-upload`, {
        method: "POST",
        headers: { Authorization: user?.token ? `Bearer ${user.token}` : "" },
        body: fd,
      });
      if (!resp.ok) {
        throw new Error((await resp.json()).detail || "Upload failed");
      }
      return resp.json(); // { job_id }
    },
    extractUploadStatus(jobId) {
      return this._json(`/extract-upload/status/${jobId}`);
    },
    // Controlled vocabulary for the document-metadata form (advisory, not enforced —
    // the backend accepts values outside these lists so a new agency never blocks an upload).
    documentMetadataVocabulary() {
      return this._json("/document-metadata/vocabulary");
    },
    confirmDocumentMetadata(payload) {
      return this._json("/document-metadata/confirm", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },
    // Filing identity for the scope, keyed by source_document (= org_files.original_name).
    listDocumentMetadata({ target_domain = null, target_org_id = null } = {}) {
      const p = new URLSearchParams();
      if (target_domain) {
        p.set("target_domain", target_domain);
      }
      if (target_org_id) {
        p.set("target_org_id", target_org_id);
      }
      return this._json(`/document-metadata${p.toString() ? `?${p}` : ""}`);
    },
    merge({ nodes, edges, source_document = null, target_domain = null, target_org_id = null }) {
      return this._json("/merge", {
        method: "POST",
        body: JSON.stringify({ nodes, edges, source_document, target_domain, target_org_id }),
      });
    },
  },

  // Save user keyword/question overrides for a chunk. null values revert to auto.
  updateChunkEnrichment: async (fileId, chunkId, { user_keywords, user_questions }) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );
      const response = await fetch(
        `${API_BASE_URL}/api/org-files/${fileId}/chunks/${chunkId}/enrichment`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: user?.token ? `Bearer ${user.token}` : "",
          },
          body: JSON.stringify({ user_keywords, user_questions }),
        }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to save enrichment");
      }
      return await response.json();
    } catch (error) {
      console.error("Error saving chunk enrichment:", error);
      throw error;
    }
  },

  // Regenerate auto keywords/questions (reprocesses the file; returns a job).
  regenerateChunkEnrichment: async (fileId, { chunkId = null, fields = null } = {}) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );
      const response = await fetch(`${API_BASE_URL}/api/org-files/${fileId}/regenerate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
        body: JSON.stringify({ chunk_id: chunkId, fields }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to regenerate enrichment");
      }
      return await response.json();
    } catch (error) {
      console.error("Error regenerating chunk enrichment:", error);
      throw error;
    }
  },

  // Check if org files feature is available
  checkOrgFilesFeature: async () => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/org-files/support`, {
        method: "GET",
        headers: {
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      // If we get 501, database is not available, fall back to legacy
      return response.status !== 501;
    } catch (error) {
      console.warn("Org files feature not available, using legacy file system");
      return false;
    }
  },

  // Alias for backward compatibility
  checkOrgFilesSupport: async () => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/org-files/support`, {
        method: "GET",
        headers: {
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      return response.status !== 501;
    } catch (error) {
      console.warn("Org files feature not available, using legacy file system");
      return false;
    }
  },

  // Corrected method name to match what the component calls
  getOrgFileStats: async (target) => {
    try {
      const params = new URLSearchParams();

      // Check if target is an org_id (starts with 'o') or a domain
      if (target.startsWith("o")) {
        params.append("org_id", target);
      } else {
        params.append("domain", target);
      }

      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/org-files/stats?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to fetch org files stats");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching org files stats:", error);
      throw error;
    }
  },

  // Knowledge Graph explorer: reads the caller's domain/org graph (same source as the
  // Profile graph manager) and normalizes edge fields to what GraphExplorer expects.
  getGraph: async ({ target_domain = null, target_org_id = null, source_document = null } = {}) => {
    const p = new URLSearchParams();
    if (target_domain) {
      p.set("target_domain", target_domain);
    }
    if (target_org_id) {
      p.set("target_org_id", target_org_id);
    }
    if (source_document) {
      p.set("source_document", source_document);
    }
    const data = await fetchApi(`/api/graph-admin/domain-graph${p.toString() ? `?${p}` : ""}`);
    return {
      ...data,
      edges: (data?.edges || []).map((e) => ({
        source_id: e.source,
        target_id: e.target,
        relationship_type: e.edge_type,
        ...e,
      })),
    };
  },

  // Built-in Tasks API methods
  getTaskSystemConfig: async () => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/builtin-tasks/config/task-system`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to fetch task system config");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching task system config:", error);
      throw error;
    }
  },

  updateBuiltinTasksConfig: async (enabled) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/builtin-tasks/config`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
        body: JSON.stringify({ enabled }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to update built-in tasks config");
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating built-in tasks config:", error);
      throw error;
    }
  },

  getBuiltinTasks: async (projectId, options = {}) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (options.workflow_id) {
        queryParams.append("workflow_id", options.workflow_id);
      }
      if (options.stage_number !== undefined && options.stage_number !== null) {
        queryParams.append("stage_number", options.stage_number);
      }
      if (options.page_size !== undefined && options.page_size !== null) {
        queryParams.append("page_size", options.page_size);
      }
      if (options.page !== undefined && options.page !== null) {
        queryParams.append("page", options.page);
      }

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/api/builtin-tasks/projects/${projectId}${queryString ? `?${queryString}` : ""}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        console.error(`[API] Error response:`, error);
        throw new Error(error.detail || "Failed to fetch built-in tasks");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching built-in tasks:", error);
      throw error;
    }
  },

  getSubmissionTasks: async (submissionId) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(
        `${API_BASE_URL}/api/faa/admin/submissions/${submissionId}/tasks`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: user?.token ? `Bearer ${user.token}` : "",
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error(`[API] Error fetching submission tasks:`, error);
        throw new Error(error.detail || "Failed to fetch submission tasks");
      }

      const result = await response.json();
      const tasks = result.data || [];
      return tasks;
    } catch (error) {
      console.error("Error fetching submission tasks:", error);
      throw error;
    }
  },

  getBuiltinTaskComments: async (taskId) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/builtin-tasks/${taskId}/comments`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to fetch task comments");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching task comments:", error);
      throw error;
    }
  },

  createBuiltinTaskComment: async (taskId, commentData) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/builtin-tasks/${taskId}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
        body: JSON.stringify(commentData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to create task comment");
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating task comment:", error);
      throw error;
    }
  },

  getBuiltinTaskAttachments: async (taskId) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/builtin-tasks/${taskId}/attachments`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to fetch task attachments");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching task attachments:", error);
      throw error;
    }
  },

  createBuiltinTaskAttachment: async (taskId, attachmentData) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const formData = new FormData();
      formData.append("file", attachmentData.file);
      if (attachmentData.comment) {
        formData.append("comment", attachmentData.comment);
      }

      const response = await fetch(`${API_BASE_URL}/api/builtin-tasks/${taskId}/attachments`, {
        method: "POST",
        headers: {
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to create task attachment");
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating task attachment:", error);
      throw error;
    }
  },

  deleteBuiltinTaskAttachment: async (attachmentId) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(
        `${API_BASE_URL}/api/builtin-tasks/attachments/${attachmentId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: user?.token ? `Bearer ${user.token}` : "",
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to delete task attachment");
      }

      return await response.json();
    } catch (error) {
      console.error("Error deleting task attachment:", error);
      throw error;
    }
  },

  downloadBuiltinTaskAttachment: async (attachmentId) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(
        `${API_BASE_URL}/api/builtin-tasks/attachments/${attachmentId}/download`,
        {
          method: "GET",
          headers: {
            Authorization: user?.token ? `Bearer ${user.token}` : "",
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to download task attachment");
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      return blobUrl;
    } catch (error) {
      console.error("Error downloading task attachment:", error);
      throw error;
    }
  },

  createBuiltinTask: async (taskData) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/builtin-tasks/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
        body: JSON.stringify(taskData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to create built-in task");
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating built-in task:", error);
      throw error;
    }
  },

  createBuiltinTasks: async (projectId, prompt) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(
        `${API_BASE_URL}/api/builtin-tasks/project/${encodeURIComponent(projectId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: user?.token ? `Bearer ${user.token}` : "",
          },
          body: JSON.stringify({ prompt }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to create built-in tasks");
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating built-in tasks:", error);
      throw error;
    }
  },

  importTasksFromPdf: async (projectId, file) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const formData = new FormData();
      formData.append("file", file);

      const authHeader = user?.token ? `Bearer ${user.token}` : "";

      // Kick off a background job — big PDFs take minutes and would otherwise
      // exceed the production proxy timeout.
      const response = await fetch(
        `${API_BASE_URL}/api/builtin-tasks/import-from-pdf/${encodeURIComponent(projectId)}`,
        {
          method: "POST",
          headers: { Authorization: authHeader },
          body: formData,
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to import tasks from PDF");
      }

      const { job_id } = await response.json();

      // Poll for the result every 20s, up to a 20-minute ceiling for very
      // large documents.
      const POLL_MS = 20000;
      const MAX_POLLS = 60;
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((resolve) => setTimeout(resolve, POLL_MS));

        const statusRes = await fetch(
          `${API_BASE_URL}/api/builtin-tasks/import-from-pdf/status/${job_id}`,
          { headers: { Authorization: authHeader } }
        );

        // A 404 can happen transiently right after POST; keep polling rather
        // than aborting the whole import on a single miss.
        if (statusRes.status === 404) {
          continue;
        }
        if (!statusRes.ok) {
          const error = await statusRes.json();
          throw new Error(error.detail || "Failed to check import status");
        }

        const job = await statusRes.json();
        if (job.status === "completed") {
          return job;
        }
        if (job.status === "failed") {
          throw new Error(job.error || "Failed to import tasks from PDF");
        }
      }

      throw new Error("PDF import timed out. The document may be too large.");
    } catch (error) {
      console.error("Error importing tasks from PDF:", error);
      throw error;
    }
  },

  bulkCreateBuiltinTasks: async (projectId, tasks) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(
        `${API_BASE_URL}/api/builtin-tasks/bulk-create/${encodeURIComponent(projectId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: user?.token ? `Bearer ${user.token}` : "",
          },
          body: JSON.stringify(tasks),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to bulk create tasks");
      }

      return await response.json();
    } catch (error) {
      console.error("Error bulk creating tasks:", error);
      throw error;
    }
  },

  generatePrecannedTasksFromCorpus: async (projectId, vehicleInfo) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(
        `${API_BASE_URL}/api/builtin-tasks/generate-from-corpus/${encodeURIComponent(projectId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: user?.token ? `Bearer ${user.token}` : "",
          },
          body: JSON.stringify(vehicleInfo),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to generate precanned tasks from corpus");
      }

      return await response.json();
    } catch (error) {
      console.error("Error generating precanned tasks from corpus:", error);
      throw error;
    }
  },

  generateSubtasks: async (parentTaskId, options = {}) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const requestBody = {
        max_subtasks: options.max_subtasks || 10,
        auto_create: options.auto_create !== undefined ? options.auto_create : true,
      };

      const response = await fetch(
        `${API_BASE_URL}/api/builtin-tasks/generate-subtasks/${encodeURIComponent(parentTaskId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: user?.token ? `Bearer ${user.token}` : "",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to generate subtasks");
      }

      return await response.json();
    } catch (error) {
      console.error("Error generating subtasks:", error);
      throw error;
    }
  },

  updateBuiltinTask: async (taskId, taskData) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/builtin-tasks/${taskId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
        body: JSON.stringify(taskData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to update built-in task");
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating built-in task:", error);
      throw error;
    }
  },

  deleteBuiltinTask: async (taskId, confirmed = false) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const url = new URL(`${API_BASE_URL}/api/builtin-tasks/${taskId}`);
      if (confirmed) {
        url.searchParams.append("confirmed", "true");
      }

      const response = await fetch(url.toString(), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to delete built-in task");
      }

      return await response.json();
    } catch (error) {
      console.error("Error deleting built-in task:", error);
      throw error;
    }
  },

  getBuiltinTask: async (taskId) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/builtin-tasks/${taskId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to fetch built-in task");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching built-in task:", error);
      throw error;
    }
  },

  getSubmittedTasks: async () => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/builtin-tasks/submitted-tasks`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to fetch submitted tasks");
      }

      const data = await response.json();
      return data.tasks || [];
    } catch (error) {
      console.error("[API] Error fetching submitted tasks:", error);
      throw error;
    }
  },

  getBuiltinAssignableUsers: async () => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetch(`${API_BASE_URL}/api/builtin-tasks/assignable-users`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to fetch assignable users");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching assignable users:", error);
      throw error;
    }
  },

  // Generic method to get tasks based on system configuration
  getTasksForProject: async (projectId, options = {}) => {
    try {
      const taskSystemConfig = await api.getTaskSystemConfig();

      if (taskSystemConfig.task_system === "builtin") {
        return await api.getBuiltinTasks(projectId, options);
      } else if (taskSystemConfig.task_system === "jira") {
        return await api.getProjectTasks(projectId); // Call the existing Jira method
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error getting project tasks:", error);
      throw error;
    }
  },

  // Generic method to get a single project task based on system configuration
  getProjectTask: async (taskId) => {
    try {
      const taskSystemConfig = await api.getTaskSystemConfig();

      if (taskSystemConfig.task_system === "builtin") {
        return await api.getBuiltinTask(taskId);
      } else if (taskSystemConfig.task_system === "jira") {
        return await api.getJiraTask(taskId);
      } else {
        throw new Error("No task system configured");
      }
    } catch (error) {
      console.error("Error getting project task:", error);
      throw error;
    }
  },

  // Generic method to update a project task based on system configuration
  updateProjectTask: async (taskId, updates) => {
    try {
      const taskSystemConfig = await api.getTaskSystemConfig();

      if (taskSystemConfig.task_system === "builtin") {
        return await api.updateBuiltinTask(taskId, updates);
      } else if (taskSystemConfig.task_system === "jira") {
        return await api.updateJiraTask(taskId, updates);
      } else {
        throw new Error("No task system configured");
      }
    } catch (error) {
      console.error("Error updating project task:", error);
      throw error;
    }
  },

  // Generic method to get assignable users based on system configuration
  getAssignableUsers: async (taskId) => {
    try {
      const taskSystemConfig = await api.getTaskSystemConfig();

      if (taskSystemConfig.task_system === "builtin") {
        // For built-in tasks, get all users in the organization
        return await api.getBuiltinAssignableUsers();
      } else if (taskSystemConfig.task_system === "jira") {
        return await api.getJiraAssignableUsers(taskId);
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error getting assignable users:", error);
      return [];
    }
  },

  // Generic method to get task comments based on system configuration
  getTaskComments: async (taskId) => {
    try {
      const taskSystemConfig = await api.getTaskSystemConfig();

      if (taskSystemConfig.task_system === "builtin") {
        return await api.getBuiltinTaskComments(taskId);
      } else if (taskSystemConfig.task_system === "jira") {
        return await api.getJiraTaskComments(taskId);
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error getting task comments:", error);
      return [];
    }
  },

  // Generic method to create task comment based on system configuration
  createTaskComment: async (taskId, commentData) => {
    try {
      const taskSystemConfig = await api.getTaskSystemConfig();

      if (taskSystemConfig.task_system === "builtin") {
        return await api.createBuiltinTaskComment(taskId, commentData);
      } else if (taskSystemConfig.task_system === "jira") {
        return await api.createJiraTaskComment(taskId, commentData);
      } else {
        throw new Error("No task system configured");
      }
    } catch (error) {
      console.error("Error creating task comment:", error);
      throw error;
    }
  },

  // Generic method to get task attachments based on system configuration
  getTaskAttachments: async (taskId) => {
    try {
      const taskSystemConfig = await api.getTaskSystemConfig();

      if (taskSystemConfig.task_system === "builtin") {
        return await api.getBuiltinTaskAttachments(taskId);
      } else if (taskSystemConfig.task_system === "jira") {
        return await api.getJiraTaskAttachments(taskId);
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error getting task attachments:", error);
      return [];
    }
  },

  // Generic method to create task attachment based on system configuration
  createTaskAttachment: async (taskId, attachmentData) => {
    try {
      const taskSystemConfig = await api.getTaskSystemConfig();

      if (taskSystemConfig.task_system === "builtin") {
        return await api.createBuiltinTaskAttachment(taskId, attachmentData);
      } else if (taskSystemConfig.task_system === "jira") {
        return await api.createJiraTaskAttachment(taskId, attachmentData);
      } else {
        throw new Error("No task system configured");
      }
    } catch (error) {
      console.error("Error creating task attachment:", error);
      throw error;
    }
  },

  // Generic method to delete task attachment based on system configuration
  deleteTaskAttachment: async (attachmentId) => {
    try {
      const taskSystemConfig = await api.getTaskSystemConfig();

      if (taskSystemConfig.task_system === "builtin") {
        return await api.deleteBuiltinTaskAttachment(attachmentId);
      } else if (taskSystemConfig.task_system === "jira") {
        // For Jira, we might need to implement this or return an error
        throw new Error("Jira attachment deletion not implemented");
      } else {
        throw new Error("No task system configured");
      }
    } catch (error) {
      console.error("Error deleting task attachment:", error);
      throw error;
    }
  },

  // Domain methods
  getDomains: async () => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetchApi(API_ENDPOINTS.domains, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      return response;
    } catch (error) {
      console.error("Error fetching domains:", error);
      throw error;
    }
  },

  createDomain: async (domainData) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetchApi(API_ENDPOINTS.domains, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
        body: JSON.stringify(domainData),
      });

      return response;
    } catch (error) {
      console.error("Error creating domain:", error);
      throw error;
    }
  },

  updateDomain: async (domainId, domainData) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetchApi(`${API_ENDPOINTS.domains}/${domainId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
        body: JSON.stringify(domainData),
      });

      return response;
    } catch (error) {
      console.error("Error updating domain:", error);
      throw error;
    }
  },

  deleteDomain: async (domainId) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      const response = await fetchApi(`${API_ENDPOINTS.domains}/${domainId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      return response;
    } catch (error) {
      console.error("Error deleting domain:", error);
      throw error;
    }
  },

  getTrainingDomains: async (orgId = null) => {
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );

      // Build URL with optional org_id parameter
      const params = new URLSearchParams();
      if (orgId) {
        params.append("org_id", orgId);
      }
      const url = `${API_BASE_URL}/api/graph-rag/domains${params.toString() ? `?${params.toString()}` : ""}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to fetch training domains");
      }

      const data = await response.json();
      // Return full domain objects (with is_org_specific flag) instead of just names
      return data.domains;
    } catch (error) {
      console.error("Error fetching training domains:", error);
      throw error;
    }
  },

  // Graph RAG domain-info: tells the ingest UI whether to render graph
  // controls for the current (domain XOR org) and what doc_category options
  // to show in the dropdown. Never throws — a failure returns the
  // no-graph shape so the ingest form still renders.
  getGraphRagDomainInfo: async ({ domain = null, orgId = null } = {}) => {
    if (!domain && !orgId) {
      return { domain: null, has_graph: false, doc_category_to_kind: {} };
    }
    try {
      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );
      const params = new URLSearchParams();
      if (domain) {
        params.append("domain", domain);
      }
      if (orgId) {
        params.append("org_id", orgId);
      }
      const url = `${API_BASE_URL}/api/graph-rag/domain-info?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: user?.token ? `Bearer ${user.token}` : "",
        },
      });
      if (!response.ok) {
        console.warn(`getGraphRagDomainInfo: HTTP ${response.status}`);
        return { domain: null, has_graph: false, doc_category_to_kind: {} };
      }
      return await response.json();
    } catch (error) {
      console.warn("getGraphRagDomainInfo failed:", error);
      return { domain: null, has_graph: false, doc_category_to_kind: {} };
    }
  },

  // Forms API methods
  getFormDomains: async () => {
    try {
      return await fetchApi("/api/forms/domains");
    } catch (error) {
      console.error("Error fetching form domains:", error);
      throw error;
    }
  },

  getFormAgencies: async (domain) => {
    try {
      const params = domain ? `?domain=${domain}` : "";
      return await fetchApi(`/api/forms/agencies${params}`);
    } catch (error) {
      console.error("Error fetching form agencies:", error);
      throw error;
    }
  },

  // Workflow methods
  getWorkflows: async () => {
    try {
      return await fetchApi(API_ENDPOINTS.workflows);
    } catch (error) {
      console.error("Error fetching workflows:", error);
      throw error;
    }
  },

  getWorkflow: async (id) => {
    try {
      return await fetchApi(`${API_ENDPOINTS.workflows}/${id}`);
    } catch (error) {
      console.error("Error fetching workflow:", error);
      throw error;
    }
  },

  createWorkflow: async (workflowData) => {
    try {
      return await fetchApi(API_ENDPOINTS.workflows, {
        method: "POST",
        body: JSON.stringify(workflowData),
      });
    } catch (error) {
      console.error("Error creating workflow:", error);
      throw error;
    }
  },

  updateWorkflow: async (id, workflowData) => {
    try {
      return await fetchApi(`${API_ENDPOINTS.workflows}/${id}`, {
        method: "PUT",
        body: JSON.stringify(workflowData),
      });
    } catch (error) {
      console.error("Error updating workflow:", error);
      throw error;
    }
  },

  deleteWorkflow: async (id) => {
    try {
      return await fetchApi(`${API_ENDPOINTS.workflows}/${id}`, {
        method: "DELETE",
      });
    } catch (error) {
      console.error("Error deleting workflow:", error);
      throw error;
    }
  },

  getFormTemplates: async (domain, agency) => {
    try {
      const params = new URLSearchParams();
      if (domain) {
        params.append("domain", domain);
      }
      if (agency) {
        params.append("agency", agency);
      }
      const queryString = params.toString();
      return await fetchApi(`/api/forms/templates${queryString ? `?${queryString}` : ""}`);
    } catch (error) {
      console.error("Error fetching form templates:", error);
      throw error;
    }
  },

  getFormTemplate: async (formName) => {
    try {
      return await fetchApi(`/api/forms/template/${formName}`);
    } catch (error) {
      console.error("Error fetching form template:", error);
      throw error;
    }
  },

  getApplications: async (projectId) => {
    try {
      return await fetchApi(`/api/forms/project/${projectId}/applications`);
    } catch (error) {
      console.error("Error fetching applications:", error);
      throw error;
    }
  },

  getApplication: async (applicationId) => {
    try {
      return await fetchApi(`/api/forms/applications/${applicationId}`);
    } catch (error) {
      console.error("Error fetching application:", error);
      throw error;
    }
  },

  createApplication: async (applicationData) => {
    try {
      return await fetchApi("/api/forms/applications", {
        method: "POST",
        body: JSON.stringify(applicationData),
      });
    } catch (error) {
      console.error("Error creating application:", error);
      throw error;
    }
  },

  updateApplication: async (applicationId, updateData) => {
    try {
      return await fetchApi(`/api/forms/applications/${applicationId}`, {
        method: "PUT",
        body: JSON.stringify(updateData),
      });
    } catch (error) {
      console.error("Error updating application:", error);
      throw error;
    }
  },

  downloadApplication: async (applicationId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/forms/applications/${applicationId}/download`,
        {
          headers: {
            Authorization: `Bearer ${JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}").token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      return response;
    } catch (error) {
      console.error("Error downloading application:", error);
      throw error;
    }
  },

  signApplication: async (applicationId, signatureData) => {
    try {
      return await fetchApi(`/api/forms/applications/${applicationId}/sign`, {
        method: "POST",
        body: JSON.stringify(signatureData),
      });
    } catch (error) {
      console.error("Error signing application:", error);
      throw error;
    }
  },

  assembleApplication: async (applicationId, assemblyData) => {
    try {
      return await fetchApi(`/api/forms/applications/${applicationId}/assemble`, {
        method: "POST",
        body: JSON.stringify(assemblyData),
      });
    } catch (error) {
      console.error("Error assembling application:", error);
      throw error;
    }
  },

  downloadApplicationAssembly: async (applicationId) => {
    try {
      const url = `${API_BASE_URL}/api/forms/applications/${applicationId}/assembly/download`;

      const user = JSON.parse(
        localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
      );
      const headers = {
        Authorization: `Bearer ${user.token}`,
      };

      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Download Error Response:", errorData);
        throw new Error(errorData.detail || `Download failed: ${response.statusText}`);
      }

      // Return the blob for file download
      return await response.blob();
    } catch (error) {
      console.error("Error downloading application assembly:", error);
      throw error;
    }
  },

  deleteApplication: async (applicationId) => {
    try {
      return await fetchApi(`/api/forms/applications/${applicationId}`, {
        method: "DELETE",
      });
    } catch (error) {
      console.error("Error deleting application:", error);
      throw error;
    }
  },

  // ==========================================
  // Domain-Agnostic Workflow Methods
  // ==========================================

  // Get workflows for a project (filtered by domain)
  getWorkflowsByProject: async (projectId, domain) => {
    try {
      const params = domain ? `?domain=${domain}` : "";
      return await fetchApi(`${API_ENDPOINTS.baseWorkflows}/${projectId}/list${params}`);
    } catch (error) {
      console.error("Error fetching workflows:", error);
      throw error;
    }
  },

  // Create a new workflow
  createDomainWorkflow: async (workflowData) => {
    try {
      return await fetchApi(`${API_ENDPOINTS.baseWorkflows}/create`, {
        method: "POST",
        body: JSON.stringify(workflowData),
      });
    } catch (error) {
      console.error("Error creating workflow:", error);
      throw error;
    }
  },

  // Update workflow
  updateDomainWorkflow: async (workflowId, updateData) => {
    try {
      return await fetchApi(`${API_ENDPOINTS.baseWorkflows}/${workflowId}/update`, {
        method: "PUT",
        body: JSON.stringify(updateData),
      });
    } catch (error) {
      console.error("Error updating workflow:", error);
      throw error;
    }
  },

  // Execute workflow stage
  executeWorkflowStage: async (executionData) => {
    try {
      return await fetchApi(`${API_ENDPOINTS.baseWorkflows}/execute-stage`, {
        method: "POST",
        body: JSON.stringify(executionData),
      });
    } catch (error) {
      console.error("Error executing stage:", error);
      throw error;
    }
  },

  // Generate tasks for a stage
  generateStageTasks: async (taskData) => {
    try {
      return await fetchApi(`${API_ENDPOINTS.baseWorkflows}/generate-tasks`, {
        method: "POST",
        body: JSON.stringify(taskData),
      });
    } catch (error) {
      console.error("Error generating tasks:", error);
      throw error;
    }
  },

  // Perform risk analysis on workflow
  performWorkflowRiskAnalysis: async (workflowId) => {
    try {
      return await fetchApi(`${API_ENDPOINTS.baseWorkflows}/${workflowId}/risk-analysis`, {
        method: "POST",
      });
    } catch (error) {
      console.error("Error performing risk analysis:", error);
      throw error;
    }
  },

  // Get workflow details
  getWorkflowDetails: async (workflowId) => {
    try {
      return await fetchApi(`${API_ENDPOINTS.baseWorkflows}/${workflowId}/details`);
    } catch (error) {
      console.error("Error fetching workflow details:", error);
      throw error;
    }
  },

  // ==========================================
  // Automotive Domain Methods
  // ==========================================

  // Classify automotive vehicle
  classifyAutomotiveVehicle: async (vehicleData) => {
    try {
      return await fetchApi(`${API_ENDPOINTS.automotive.classification}/classify`, {
        method: "POST",
        body: JSON.stringify(vehicleData),
      });
    } catch (error) {
      console.error("Error classifying vehicle:", error);
      throw error;
    }
  },

  // Validate automotive classification
  validateAutomotiveClassification: async (vehicleData) => {
    try {
      return await fetchApi(`${API_ENDPOINTS.automotive.classification}/validate`, {
        method: "POST",
        body: JSON.stringify(vehicleData),
      });
    } catch (error) {
      console.error("Error validating classification:", error);
      throw error;
    }
  },

  // Get vehicle classes
  getVehicleClasses: async () => {
    try {
      return await fetchApi(`${API_ENDPOINTS.automotive.classification}/vehicle-classes`);
    } catch (error) {
      console.error("Error fetching vehicle classes:", error);
      throw error;
    }
  },

  // Get propulsion types
  getPropulsionTypes: async () => {
    try {
      return await fetchApi(`${API_ENDPOINTS.automotive.classification}/propulsion-types`);
    } catch (error) {
      console.error("Error fetching propulsion types:", error);
      throw error;
    }
  },
};

export { API_ENDPOINTS, fetchApi, fetchImage, getOrgLogoUrl, api };
